import React, { useState } from "react";
import { api, arabicError } from "../lib/api";
import { toast } from "sonner";
import { X, Save } from "lucide-react";
import { PRODUCT_TYPES, PRODUCT_CATEGORIES, PRODUCT_COLORS, COUNTRIES, unitDisplay, isCountUnit, badCountQty, INT_QTY_MSG } from "../lib/constants";
import SelectWithOther from "./SelectWithOther";

// codeOnly: same modal for a same-store employee — only كود المنتج is editable (backend enforces the same restriction)
const EditPhotoModal = ({ photo, onClose, onSaved, codeOnly = false }) => {
  const [form, setForm] = useState({
    product_code: photo.product_code || "",
    category: photo.category || PRODUCT_CATEGORIES[0],
    type: photo.type || PRODUCT_TYPES[0],
    country: photo.country || COUNTRIES[0],
    size: photo.size || "",
    color: photo.color || "",
    manufacturer: photo.manufacturer || "",
    price_usd: photo.price_usd ?? photo.price ?? 0,
    quantity_available: photo.quantity_available ?? photo.quantity ?? 0,
    cost_usd: photo.cost_usd ?? "",
    low_stock_threshold: photo.low_stock_threshold ?? "",
  });
  const [saving, setSaving] = useState(false);
  const ud = unitDisplay(photo.unit);                                     // Phase 5C-2B: labels only; unit is never sent/changed here
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const code = form.product_code.trim();
    if (!code) { toast.error("كود المنتج مطلوب ولا يمكن تركه فارغاً"); return; }
    if (!codeOnly && (!form.type.trim() || !form.category.trim())) { toast.error("يرجى إدخال نوع المنتج والصنف"); return; }
    if (!codeOnly && badCountQty(photo.unit, form.quantity_available)) { toast.error(INT_QTY_MSG); return; }
    setSaving(true);
    try {
      await api.patch(`/photos/${photo.id}`, codeOnly ? { product_code: code } : {
        ...form,
        product_code: code,
        price_usd: parseFloat(form.price_usd),
        quantity_available: parseFloat(form.quantity_available),
        cost_usd: form.cost_usd === "" ? 0 : parseFloat(form.cost_usd),
        low_stock_threshold: form.low_stock_threshold === "" ? -1 : parseFloat(form.low_stock_threshold),
      });
      toast.success("تم حفظ التعديلات");
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(arabicError(err));
    } finally { setSaving(false); }
  };

  const input = "h-11 w-full rounded-lg border border-[#7AB2B2] bg-white px-3 focus:outline-none focus:ring-2 focus:ring-[#09637E]";

  return (
    <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4" onClick={onClose} dir="rtl">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[calc(100vh-2rem)] md:max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="edit-photo-modal">
        <div className="sticky top-0 bg-white p-4 border-b border-[#7AB2B2] flex items-center justify-between">
          <h3 className="text-xl font-extrabold text-[#333333]">{codeOnly ? "تعديل كود المنتج" : "تعديل معلومات الصورة"}</h3>
          <button onClick={onClose} className="w-10 h-10 rounded-lg hover:bg-[#EBF4F6] flex items-center justify-center"><X /></button>
        </div>
        <form onSubmit={submit} className="p-4 pb-6 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-sm font-bold text-[#333333] mb-1">كود المنتج</label>
              <input className={input} dir="ltr" maxLength={60} value={form.product_code} onChange={(e) => set("product_code", e.target.value)} required data-testid="edit-product-code" />
              <p className="text-[11px] text-[#777777] mt-1">يجب أن يكون الكود فريداً؛ لا يمكن تركه فارغاً.</p>
            </div>
            {!codeOnly && <>
            <div>
              <label className="block text-sm font-bold text-[#333333] mb-1">نوع المنتج</label>
              <SelectWithOther value={form.type} onChange={(v) => set("type", v)} options={PRODUCT_TYPES}
                className={input} testid="edit-type" placeholder="اكتب نوع المنتج..." />
            </div>
            <div>
              <label className="block text-sm font-bold text-[#333333] mb-1">الصنف</label>
              <SelectWithOther value={form.category} onChange={(v) => set("category", v)} options={PRODUCT_CATEGORIES}
                className={input} testid="edit-category" placeholder="اكتب الصنف..." />
            </div>
            <div>
              <label className="block text-sm font-bold text-[#333333] mb-1">بلد المنشأ</label>
              <select className={input} value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="edit-country">
                {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-[#333333] mb-1">المقاس</label>
              <input className={input} value={form.size} onChange={(e) => set("size", e.target.value)} required data-testid="edit-size" />
            </div>
            <div>
              <label className="block text-sm font-bold text-[#333333] mb-1">اللون</label>
              <select className={input} value={form.color} onChange={(e) => set("color", e.target.value)} data-testid="edit-color">
                <option value="">— لم يُحدَّد —</option>
                {PRODUCT_COLORS.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-[#333333] mb-1">الشركة المصنعة</label>
              <input className={input} value={form.manufacturer} onChange={(e) => set("manufacturer", e.target.value)} data-testid="edit-manufacturer" />
            </div>
            <div>
              <label className="block text-sm font-bold text-[#333333] mb-1" data-testid="edit-price-label">السعر بالدولار {ud.per} *</label>
              <input type="number" step="0.01" min="0" className={input} value={form.price_usd} onChange={(e) => set("price_usd", e.target.value)} required data-testid="edit-price" />
            </div>
            <div>
              <label className="block text-sm font-bold text-[#333333] mb-1" data-testid="edit-quantity-label">الكمية المتوفرة ({ud.short})</label>
              <input type="number" step={isCountUnit(photo.unit) ? "1" : "0.1"} min="0" className={input} value={form.quantity_available} onChange={(e) => set("quantity_available", e.target.value)} required data-testid="edit-quantity" />
            </div>
            <div className="sm:col-span-2 bg-[#F5FAFB] border border-[#DCEBEE] rounded-lg p-3">
              <label className="block text-sm font-bold text-[#333333] mb-1" data-testid="edit-cost-label">رأس المال (التكلفة {ud.per} $) — خاص بك فقط</label>
              <input type="number" step="0.01" min="0" className={input} placeholder="اختياري — يُستخدم لحساب الربح في إحصائياتي" value={form.cost_usd} onChange={(e) => set("cost_usd", e.target.value)} data-testid="edit-cost" />
              <p className="text-[11px] text-[#777777] mt-1">لا يظهر للموظفين ولا في البحث ولا في صفحة المتجر العامة. اتركه فارغاً إن كان غير معروف.</p>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-bold text-[#333333] mb-1" data-testid="edit-low-stock-label">حد تنبيه المخزون لهذا المنتج ({ud.short}) — اختياري</label>
              <input type="number" step="1" min="0" className={input} placeholder="فارغ = استخدام الحد الافتراضي للمتجر" value={form.low_stock_threshold} onChange={(e) => set("low_stock_threshold", e.target.value)} data-testid="edit-low-stock-threshold" />
            </div>
            </>}
          </div>
          <button type="submit" disabled={saving} data-testid="edit-submit"
            className="w-full h-12 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-extrabold flex items-center justify-center gap-2 disabled:opacity-50">
            <Save size={18} /> {saving ? "جارٍ الحفظ..." : "حفظ التعديلات"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EditPhotoModal;
